import { Key } from "react";
import { ComponentNames } from "../constant/component-constant";
import ReportingService from "../service/reporting-service";
import {
  ComponentTypeReportingDefaults,
  ReportingDefaults
} from "../constant/analytics-mapper";
import { logAdapterEvent } from "./logger";

/**
 * Field names whose list stays a list even at length 1. Their readers call
 * `Array.isArray` or `.map`, so a collapsed single entry reads as absent and
 * the section renders empty in silence.
 *
 * ⚠ BY NAME, SO IT APPLIES TO EVERY `arraytoObj` CALLER — `commonSbFixer` and
 * the v2 cases that call it directly, not just the v3 bloks these names come
 * from. A name added here must be one that NO caller wants collapsed.
 */
const KEEP_AS_LIST = new Set([
  "cards",
  "panels",
  "channel_logos",
  "not_included_labels",
  "excluded_svods",
  "excluded_packages",
  "compare_section1_chips",
  "compare_section2_chips",
  "compare_section3_chips",
  "compare_default_package_ids"
]);

export function arraytoObj(obj, k?: Key, sbPreviewAllowed?: boolean) {
  if (!sbPreviewAllowed) {
    delete obj?._uid;
  }
  if (Array.isArray(obj)) {
    if (
      obj.length === 1 &&
      !KEEP_AS_LIST.has(k?.toString()) &&
      !k?.toString()?.startsWith?.("array_")
    ) {
      return obj.map(v => (v && typeof v === "object" ? arraytoObj(v, "", sbPreviewAllowed) : v))[0];
    }
    return obj.map(v => (v && typeof v === "object" ? arraytoObj(v, "", sbPreviewAllowed) : v));
  }

  return obj?.isEvolv ?
    obj :
    Object.entries(obj)
      .map(([k, v]) => [
        k,
        v && typeof v === "object" ? arraytoObj(v, k, sbPreviewAllowed) : v
      ])
      .reduce(
        (a, [k, v]) => (!(v || v === true || v === false) ? a : ((a[k] = v), a)),
        {}
      );
}

export function customArraytoObj(obj, k?: Key) {
  if (Array.isArray(obj)) {
    if (
      obj.length === 1 &&
      k !== "cards" &&
      !k?.toString()?.startsWith?.("array_")
    ) {
      return obj.map(v => (v && typeof v === "object" ? arraytoObj(v) : v))[0];
    }
    return obj.map(v => (v && typeof v === "object" ? arraytoObj(v) : v));
  }

  return Object.entries(obj)
    .map(([k, v]) => [
      k,
      v &&
      typeof v === "object" &&
      obj?.type === "SHOWCAROUSAL" &&
      !["carousels", "ctas"].includes(k) ?
        arraytoObj(v, k) :
        v
    ])
    .reduce(
      (a, [k, v]) => (!(v || v === true || v === false) ? a : ((a[k] = v), a)),
      {}
    );
}

export function cleanEmpty(obj, sbPreviewAllowed?: boolean) {
  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return null;
    }
    return obj
      .map(v => (typeof v === "object" || Array.isArray(v) ?
        cleanEmpty(v, sbPreviewAllowed) :
        v))
      .filter(v => !(v == null));
  }
  if (obj?.component === "content_uf" || obj?.component === "content") {
    delete obj?.fade;
  }
  if (obj?.component === "button" || obj?.component === "cta") {
    delete obj?.fade;
    delete obj?.visible;
    delete obj?.hasSound;
    delete obj?.isFloatingImage;
    delete obj?.isImage;
    delete obj?.modeafullWidth;
  }

  delete obj.component;
  if (!sbPreviewAllowed) {
    delete obj._uid;
  }

  if (obj.componentType) {
    obj.component = obj.componentType;
    delete obj.componentType;
  }

  // If reference content key is present inside object, will assume it is reference component from storyblok and remove reference content key from object and assign content to key directly
  let resolvedObj = obj;
  if (
    obj?.reference?.content &&
    typeof obj?.reference?.content == "object" &&
    Object.keys(obj?.reference?.content).length > 0
  ) {
    const isCustomComponent = obj?.isCustomComponent;
    resolvedObj = obj?.reference?.content;
    if (isCustomComponent) {
      resolvedObj.isCustomComponent = isCustomComponent;
    }
  }

  return Object.entries(resolvedObj)
    .map(([k, v]) => [
      k.replaceAll("array_", ""),
      v && typeof v === "object" ? cleanEmpty(v, sbPreviewAllowed) : v
    ])
    .reduce(
      (a, [k, v]) => (!(v || v === true || v === false) ? a : ((a[k] = v), a)),
      {}
    );
}

export const commonSbFixer = (
  componentProps: any,
  index?: any,
  sbPreviewAllowed?: boolean,
  _priorityMap?: any,
  packageConfig?: any,
  cmsPageName?: string,
  rsnTierLegalTextObj?: any
): any => {
  try {
    const content = cleanEmpty(
      arraytoObj(componentProps, "", sbPreviewAllowed),
      sbPreviewAllowed
    );

    if(!content) {
      logAdapterEvent("CommonSbFixer", "Content is empty after cleaning. Returning null.", {
        cmsPageName,
        componentProps
      });
      return null;
    }

    // const content = componentProps?.type === 'SHOWCAROUSAL' ? cleanEmpty(customArraytoObj(componentProps)) : cleanEmpty(arraytoObj(componentProps));
    // const type = WnCComponentNames[content.type];
    const type =
      content?.variant === "toggle-package-card" ?
        ComponentNames.TOGGLE_PLAN_CARD :
        content?.type === "PLANCARD" && content?.orientation === "horizontal" ?
          ComponentNames.HORIZONTAL_PLAN_CARD :
          ComponentNames[content.type];
    let data;
    try {
      const analytics = {
        ...ReportingDefaults[ComponentTypeReportingDefaults?.[type]]
      };

      data = new ReportingService().fixAnalyticsValues(
        content,
        analytics,
        type,
        index,
        null,
        "STORYBLOK"
      );
    } catch (err) {
      console.error("Error in ReportingService fixAnalyticsValues: ", err, cmsPageName);
    }

    if (content.type === "LOCALRSNTOOL" && rsnTierLegalTextObj) {
      content.rsnTierLegalTextObj = rsnTierLegalTextObj;
    }
    if (
      (content.type === "PLANCARD" || content.type === "REVISITORMODAL") &&
      packageConfig
    ) {
      content.packageConfig = packageConfig;
    }
    // WAT-10279: priorityMap is sorted and passed to the orchestrator
    // (modal-orchestrator) at the page level via getServerSideProps /
    // modal-orchestrator.client.tsx. Individual modal components don't need
    // the priority list injected into their own props.
    return {
      props: {
        ...content,
        ...(content.options && { gnavOptions: content.options }),
        ...(data?.analytics && { analytics: data?.analytics }),
        options: {
          id: content?.id || null,
          ...(content.options && { ...content.options })
        }
      },
      type
    };
  } catch (error) {
    console.error(
      `CommonSbFixer Error: ${  error  } Props: ${  JSON.stringify(componentProps)   }cmsPageName${ cmsPageName}`
    );
  }
};

export const parseGlobalSBFeed = feed => {
  const body = feed?.story?.content?.Body;
  const globalFeed =
    body?.length > 0 &&
    body.filter(
      item => item?.type === "GLOBALHEADER" ||
        item?.type === "LINKFARM" ||
        item?.type === "GLOBALFOOTER" ||
        item?.component === "uf-global-header" ||
        item?.component === "globalFooter" ||
        item?.type === "EXITMODALV2" ||
        item?.component === "idleTimerModal" ||
        item?.component === "pageContent" ||
        item?.component === "signalStrengthContent" ||
        item?.component === "packageConfig" ||
        item?.component === "myHomeTeamOutOfMarketRSNs" ||
        item?.component === "subscriptions_uf" ||
        item?.component === "genreNationalMapping" ||
        item?.component === "dtvRevisiterModal" ||
        item?.component === "personalizationMapping" ||
        item?.component === "layoutComponent"
    );
  const parsedGlobalFeed = cleanEmpty(arraytoObj(globalFeed));
  return parsedGlobalFeed;
};

/**
 * A v3 chrome story's single blok, in the organism's own prop names.
 *
 * Not `parseGlobalSBFeed`: that filters `Body` by a v2 `type`/`component`
 * allowlist, and the DDS bloks carry no `type` at all — every one of them
 * would be filtered out and the result would be null. The pipeline is
 * otherwise identical, and it is what turns `array_links` into `links`:
 * `arraytoObj` leaves an `array_`-prefixed list as an array instead of
 * collapsing a single entry, then `cleanEmpty` strips the prefix.
 */
export const parseV3ChromeBlok = feed => {
  const blok = feed?.story?.content?.Body?.[0];
  return blok ? cleanEmpty(arraytoObj(blok)) : null;
};

export const parseSvodGenreSBFeed = feedRes => {
  if (feedRes) {
    const body = feedRes?.story?.content?.body;
    const parsedFeed = cleanEmpty(arraytoObj(body));
    return parsedFeed;
  }
};

export const removeUidFromObject = (obj: any): any => {
  if (Array.isArray(obj)) {
    // If the object is an array, recursively clean each element
    return obj.map(removeUidFromObject);
  } else if (typeof obj === "object" && obj !== null) {
    // If the object is an object, recursively clean each key-value pair
    const cleanedObj: any = {};
    for (const key in obj) {
      if (key !== "_uid") {
        cleanedObj[key] = removeUidFromObject(obj[key]);
      }
    }
    return cleanedObj;
  }
  // If it's neither an array nor an object, return it as is
  return obj;
};
